import React, { useState, useEffect, useCallback, useContext } from 'react';
import { AuthContext } from '../App';
import apiClient from '../utils/apiClient';
import { isGuest as checkIsGuest, getRolesDisplay } from '../utils/roleUtils';
import ConfirmationModal from '../components/ConfirmationModal';
import {
  CameraIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  UserIcon,
  TrashIcon,
  KeyIcon,
} from '@heroicons/react/24/outline';
import { startRegistration, browserSupportsWebAuthn } from '@simplewebauthn/browser';

const CARD_BG = '#1c2432';

const Profile: React.FC = () => {
  const { user, BASE_URL } = useContext(AuthContext);
  const isGuest = checkIsGuest(user);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState<any>({});
  const [displayData, setDisplayData] = useState<any>({});
  const [loading, setLoading] = useState(true);
  const [profileImageUrl, setProfileImageUrl] = useState<string | null>(null);
  const [imageLoading, setImageLoading] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [confirmModal, setConfirmModal] = useState({
    visible: false,
    type: 'info' as 'success' | 'warning' | 'error' | 'question' | 'info',
    title: '',
    message: '',
    variant: 'single' as 'single' | 'two',
    onConfirm: () => {},
  });
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [passkeys, setPasskeys] = useState<
    Array<{
      id: number;
      device_name: string | null;
      transports: string | null;
      backed_up: number;
      created_at: string;
      last_used_at: string | null;
    }>
  >([]);
  const [passkeyBusy, setPasskeyBusy] = useState(false);
  const passkeySupported = browserSupportsWebAuthn();

  useEffect(() => {
    fetchProfileData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadPasskeys = useCallback(async () => {
    try {
      const res = await apiClient.get('/api/webauthn/credentials');
      if (res.data.success) setPasskeys(res.data.passkeys || []);
    } catch (err) {
      console.warn('[Profile] Passkeys konnten nicht geladen werden', err);
    }
  }, []);

  useEffect(() => {
    if (passkeySupported) loadPasskeys();
  }, [passkeySupported, loadPasskeys]);

  const handleAddPasskey = async () => {
    setPasskeyBusy(true);
    try {
      const beginRes = await apiClient.post('/api/webauthn/register/begin', {});
      const credential = await startRegistration({ optionsJSON: beginRes.data });
      const deviceName =
        (typeof navigator !== 'undefined' && (navigator as any).userAgentData?.platform) ||
        navigator?.platform ||
        'Unbekanntes Gerät';
      const completeRes = await apiClient.post('/api/webauthn/register/complete', {
        credential,
        deviceName: deviceName.toString().slice(0, 60),
      });
      if (completeRes.data.success) {
        await loadPasskeys();
        setConfirmModal({
          visible: true,
          type: 'success',
          title: 'Passkey gespeichert',
          message: 'Du kannst dich nun mit Passkey anmelden.',
          variant: 'single',
          onConfirm: () => setConfirmModal((prev) => ({ ...prev, visible: false })),
        });
      }
    } catch (err: any) {
      if (err?.name !== 'NotAllowedError' && err?.name !== 'AbortError') {
        console.error('[Profile] Passkey hinzufügen fehlgeschlagen:', err);
        setConfirmModal({
          visible: true,
          type: 'error',
          title: 'Fehler',
          message:
            err?.response?.data?.error ||
            err?.message ||
            'Passkey konnte nicht hinzugefügt werden.',
          variant: 'single',
          onConfirm: () => setConfirmModal((prev) => ({ ...prev, visible: false })),
        });
      }
    } finally {
      setPasskeyBusy(false);
    }
  };

  const handleDeletePasskey = async (id: number) => {
    setPasskeyBusy(true);
    try {
      await apiClient.delete(`/api/webauthn/credentials/${id}`);
      await loadPasskeys();
    } catch (err: any) {
      console.error('[Profile] Passkey löschen fehlgeschlagen:', err);
    } finally {
      setPasskeyBusy(false);
    }
  };

  useEffect(() => {
    if (formData.mg_nr && formData.foto_path) {
      const token = localStorage.getItem('jwt_token');
      const timestamp = formData.foto_path_updated || Date.now();
      const url = `${BASE_URL}/api/members/user-image-url/${formData.mg_nr}?size=L&token=${token}&ts=${timestamp}`;
      setProfileImageUrl(url);
      setImageError(false);
    } else {
      setProfileImageUrl(null);
    }
  }, [formData.mg_nr, formData.foto_path, formData.foto_path_updated, BASE_URL]);

  const fetchProfileData = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/api/members/profile');
      const data = res.data;
      const newFormData = {
        vorname: data.vorname || '',
        name: data.name || '',
        strasse: data.strasse || '',
        plz: data.plz || '',
        ort: data.ort || '',
        tel_nummer: data.tel_nummer || '',
        email: data.email || '',
        geb_am: data.geb_am || '',
        eintritt: data.eintritt || '',
        mitglied_nr: data.mitglied_nr || '',
        mg_nr: data.mg_nr || '',
        vereinsposition: 'Mitglied',
        beitrag_bezahlt: data.beitrag_bezahlt,
        beitrag_datum: data.beitrag_datum,
        beitrag_betrag: data.beitrag_betrag,
        beitrag_jahr: data.beitrag_jahr || new Date().getFullYear(),
        datenschutzerklaerung: data.datenschutzerklaerung || 0,
        homepage: data.homepage || 0,
        facebook: data.facebook || 0,
        presse: data.presse || 0,
        vorstand: data.vorstand || 0,
        foto_path: data.foto_path || '',
        foto_path_updated: data.foto_path_updated || null,
        gender: data.gender !== undefined && data.gender !== null ? data.gender : null,
      };
      setFormData(newFormData);
      setDisplayData({
        ...newFormData,
        geb_am: formatGermanDate(data.geb_am),
        eintritt: formatGermanDate(data.eintritt),
        gender: newFormData.gender,
      });

      let rolesString = 'Mitglied';
      if (user?.roles && Array.isArray(user.roles)) {
        rolesString = getRolesDisplay(user);
      } else if (data.roles && Array.isArray(data.roles)) {
        rolesString = getRolesDisplay(data);
      } else if (data.mg_nr) {
        try {
          const rolesRes = await apiClient.get(`/api/roles/user_roles?mg_nr=${data.mg_nr}`);
          const rolesData = rolesRes.data;
          if (rolesData.success) {
            rolesString = rolesData.roles.join(', ') || 'Mitglied';
          }
        } catch (error: any) {
          if (error.response?.status !== 401) {
            console.error('Fehler beim Abrufen der Rollen:', error);
          }
          rolesString = getRolesDisplay(data);
        }
      }

      setFormData((prev: any) => ({ ...prev, vereinsposition: rolesString }));
      setDisplayData((prev: any) => ({ ...prev, vereinsposition: rolesString }));
    } catch (err: any) {
      if (err.response?.status !== 401) {
        console.error('Fehler beim Abrufen der Profildaten:', err);
        setConfirmModal({
          visible: true,
          type: 'error',
          title: 'Fehler',
          message: 'Fehler beim Laden der Profildaten.',
          variant: 'single',
          onConfirm: () => {},
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const { name, value } = e.target;
      setFormData((prev: any) => ({ ...prev, [name]: value }));
      if (name === 'geb_am' || name === 'eintritt') {
        setDisplayData((prev: any) => ({ ...prev, [name]: formatGermanDate(value) }));
      } else {
        setDisplayData((prev: any) => ({ ...prev, [name]: value }));
      }
    },
    []
  );

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setLoading(true);
    try {
      const submitData = {
        ...formData,
        geb_am: formData.geb_am ? new Date(formData.geb_am).toISOString().split('T')[0] : '',
        eintritt: formData.eintritt
          ? new Date(formData.eintritt).toISOString().split('T')[0]
          : '',
      };
      await apiClient.put(`/api/members/user/${formData.mg_nr}`, submitData);
      setConfirmModal({
        visible: true,
        type: 'success',
        title: 'Erfolgreich',
        message: 'Profil erfolgreich aktualisiert!',
        variant: 'single',
        onConfirm: () => {},
      });
      setEditMode(false);
      setDisplayData((prev: any) => ({
        ...prev,
        geb_am: formatGermanDate(formData.geb_am),
        eintritt: formatGermanDate(formData.eintritt),
        gender: formData.gender,
      }));
    } catch (err: any) {
      if (err.response?.status !== 401) {
        const errorMessage = err.response?.data?.message || '😓 Fehler beim Speichern.';
        setConfirmModal({
          visible: true,
          type: 'error',
          title: 'Fehler',
          message: errorMessage,
          variant: 'single',
          onConfirm: () => {},
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageLoading(true);
    const uploadData = new FormData();
    uploadData.append('photo', file);
    try {
      const res = await apiClient.post('/api/auth/upload_photo', uploadData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const data = res.data;
      const newTimestamp = Date.now();
      setFormData((prev: any) => ({
        ...prev,
        foto_path: data.file_path,
        foto_path_updated: newTimestamp,
      }));
      setDisplayData((prev: any) => ({
        ...prev,
        foto_path: data.file_path,
        foto_path_updated: newTimestamp,
      }));
      const token = localStorage.getItem('jwt_token');
      const newUrl = `${BASE_URL}/api/members/user-image-url/${formData.mg_nr}?size=L&token=${token}&ts=${newTimestamp}&nocache=${Date.now()}`;
      setProfileImageUrl(newUrl);
      setConfirmModal({
        visible: true,
        type: 'success',
        title: 'Erfolg',
        message: '🎉 Foto erfolgreich hochgeladen!',
        variant: 'single',
        onConfirm: () => {},
      });
    } catch (err: any) {
      if (err.response?.status !== 401) {
        const errorMessage = err.response?.data?.message || '😓 Fehler beim Hochladen.';
        setConfirmModal({
          visible: true,
          type: 'error',
          title: 'Fehler',
          message: errorMessage,
          variant: 'single',
          onConfirm: () => {},
        });
      }
    } finally {
      setImageLoading(false);
      const fileInput = document.getElementById('image-upload') as HTMLInputElement;
      if (fileInput) fileInput.value = '';
    }
  };

  const handlePhotoDelete = async () => {
    setConfirmModal({
      visible: true,
      type: 'warning',
      title: 'Profilbild löschen',
      message: 'Möchten Sie Ihr Profilbild wirklich löschen?',
      variant: 'two',
      onConfirm: async () => {
        setImageLoading(true);
        try {
          const res = await apiClient.delete('/api/members/delete-image');
          if (res.data.success) {
            setFormData((prev: any) => ({
              ...prev,
              foto_path: null,
              foto_path_updated: null,
            }));
            setDisplayData((prev: any) => ({
              ...prev,
              foto_path: null,
              foto_path_updated: null,
            }));
            setProfileImageUrl(null);
            setImageError(false);
            setConfirmModal({
              visible: true,
              type: 'success',
              title: 'Erfolg',
              message: 'Profilbild wurde gelöscht.',
              variant: 'single',
              onConfirm: () => {},
            });
          }
        } catch (err: any) {
          if (err.response?.status !== 401) {
            const errorMessage =
              err.response?.data?.message || 'Fehler beim Löschen des Profilbilds.';
            setConfirmModal({
              visible: true,
              type: 'error',
              title: 'Fehler',
              message: errorMessage,
              variant: 'single',
              onConfirm: () => {},
            });
          }
        } finally {
          setImageLoading(false);
        }
      },
    });
  };

  const handlePasswordChange = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setConfirmModal({
        visible: true,
        type: 'error',
        title: 'Fehler',
        message: 'Die neuen Passwörter stimmen nicht überein',
        variant: 'single',
        onConfirm: () => {},
      });
      return;
    }
    try {
      const res = await apiClient.post('/api/members/change_password', {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
      });
      if (res.data.success) {
        setConfirmModal({
          visible: true,
          type: 'success',
          title: 'Erfolg',
          message: '🎉 Passwort erfolgreich geändert!',
          variant: 'single',
          onConfirm: () => {},
        });
        setShowPasswordModal(false);
        setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      }
    } catch (error: any) {
      setConfirmModal({
        visible: true,
        type: 'error',
        title: 'Fehler',
        message: error.response?.data?.message || 'Fehler beim Ändern des Passworts',
        variant: 'single',
        onConfirm: () => {},
      });
    }
  };

  const formatGermanDate = (dateStr: string) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const formatLongDate = (dateStr: string) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  };

  // Profile completeness percentage
  const completionFields = isGuest
    ? ['vorname', 'name', 'email', 'strasse', 'plz', 'ort', 'tel_nummer', 'foto_path']
    : ['vorname', 'name', 'email', 'strasse', 'plz', 'ort', 'tel_nummer', 'eintritt', 'foto_path'];
  const completedCount = completionFields.filter((f) => formData[f]).length;
  const completionPct = completionFields.length > 0
    ? Math.round((completedCount / completionFields.length) * 100)
    : 0;
  const isComplete = completionPct === 100;
  const completionDeg = (completionPct / 100) * 360;
  const completionColor = completionPct >= 90 ? '#34d399' : completionPct >= 70 ? '#fbbf24' : '#f87171';

  // Membership years
  const memberYears = formData.eintritt
    ? Math.floor(
        (Date.now() - new Date(formData.eintritt).getTime()) / (1000 * 60 * 60 * 24 * 365.25)
      )
    : 0;
  const memberDays = formData.eintritt
    ? Math.floor((Date.now() - new Date(formData.eintritt).getTime()) / (1000 * 60 * 60 * 24))
    : 0;

  const genderLabel =
    formData.gender === false || formData.gender === 0
      ? 'Männlich'
      : formData.gender === true || formData.gender === 1
      ? 'Weiblich'
      : 'Nicht angegeben';

  // ----- LOADING STATE -----
  if (loading && !formData.vorname) {
    return (
      <div className="min-h-screen" style={{ background: 'transparent' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-8 sm:pb-10 space-y-6">
          <div
            className="rounded-3xl p-8 animate-pulse bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
          >
            <div className="h-8 w-48 bg-gray-200 dark:bg-slate-700/60 rounded mb-3" />
            <div className="h-4 w-72 bg-gray-200 dark:bg-slate-700/40 rounded" />
          </div>
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="rounded-2xl p-5 animate-pulse bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
            >
              <div className="h-5 w-2/3 bg-gray-200 dark:bg-slate-700/60 rounded mb-2" />
              <div className="h-4 w-1/3 bg-gray-200 dark:bg-slate-700/40 rounded" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ----- HELPERS -----
  const FieldTile: React.FC<{
    label: string;
    value: React.ReactNode;
    name?: string;
    editable?: boolean;
    inputType?: string;
    helper?: React.ReactNode;
  }> = ({ label, value, name, editable, inputType = 'text', helper }) => {
    const isInput = editMode && editable && name;
    return (
      <div
        className={`px-3 py-2.5 rounded-xl transition bg-gray-50 dark:bg-slate-900/40 ${
          isInput
            ? 'border border-primary-500/40'
            : 'border border-gray-200 dark:border-slate-700/60'
        }`}
      >
        <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold">{label}</div>
        {isInput ? (
          <input
            type={inputType}
            name={name}
            value={formData[name] ?? ''}
            onChange={handleInputChange}
            className="mt-1 w-full bg-transparent text-sm font-bold text-gray-900 dark:text-white outline-none"
          />
        ) : (
          <div className="text-sm font-bold text-gray-900 dark:text-white mt-0.5 break-words">
            {value || <span className="text-gray-500 dark:text-slate-500 font-normal italic">Nicht angegeben</span>}
          </div>
        )}
        {helper && <div className="text-[10px] text-gray-500 dark:text-slate-500 mt-1">{helper}</div>}
      </div>
    );
  };

  const Toggle: React.FC<{ active: boolean; label: string; sublabel?: string }> = ({
    active,
    label,
    sublabel,
  }) => (
    <div
      className={`flex items-center justify-between px-3 py-3 rounded-xl ${
        active
          ? 'bg-primary-500/5 border border-primary-500/20'
          : 'bg-gray-50 dark:bg-slate-900/40 border border-gray-200 dark:border-slate-700/60'
      }`}
    >
      <div>
        <div className="text-sm font-bold text-gray-900 dark:text-white">{label}</div>
        {sublabel && <div className="text-[10px] text-gray-600 dark:text-slate-400 mt-0.5">{sublabel}</div>}
      </div>
      <div
        className="w-10 h-6 rounded-full relative transition"
        style={{ background: active ? '#10b981' : '#334155' }}
      >
        <div
          className="absolute top-0.5 w-5 h-5 rounded-full shadow transition"
          style={{
            background: active ? '#fff' : '#94a3b8',
            left: active ? 'auto' : '2px',
            right: active ? '2px' : 'auto',
          }}
        />
      </div>
    </div>
  );

  return (
    <div
      className="min-h-screen font-sans text-gray-900 dark:text-slate-200"
      style={{ background: 'transparent', WebkitFontSmoothing: 'antialiased' }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-8 sm:pb-10">
        {/* INTRO: Kicker + Story */}
        <section className="pt-2 pb-6">
          <div className="text-[11px] uppercase tracking-[0.22em] text-primary-600/80 dark:text-primary-400/80 font-semibold mb-3">
            {user?.mitglied_nr ? `Mitglied #${user.mitglied_nr} · ` : ''}Stand{' '}
            {new Date().toLocaleDateString('de-DE', { month: 'long', year: 'numeric' })}
          </div>
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-black text-gray-900 dark:text-white leading-[1.05] tracking-tight mb-4">
            Mein <span className="text-primary-600 dark:text-primary-400">Profil.</span>
          </h1>
          <p className="text-gray-700 dark:text-slate-300 text-base md:text-lg leading-relaxed max-w-2xl">
            {(() => {
              const parts: React.ReactNode[] = [];
              const greeting = formData.vorname ? `Hi ${formData.vorname}, ` : '';

              if (isComplete && (isGuest || formData.beitrag_bezahlt)) {
                parts.push(
                  <React.Fragment key="all-good">
                    {greeting}du bist startklar — <span className="font-bold text-primary-700 dark:text-primary-300">Profil komplett</span>
                    {!isGuest && formData.beitrag_bezahlt && (
                      <>, Beitrag {formData.beitrag_jahr} bezahlt</>
                    )}
                    {passkeys.length > 0 && (
                      <>
                        , <span className="font-bold text-gray-900 dark:text-white">
                          {passkeys.length} {passkeys.length === 1 ? 'Passkey' : 'Passkeys'}
                        </span>{' '}
                        aktiv
                      </>
                    )}
                  </React.Fragment>
                );
              } else {
                if (!isComplete) {
                  const missing = completionFields.length - completedCount;
                  parts.push(
                    <React.Fragment key="incomplete">
                      {greeting}dein Profil ist zu{' '}
                      <span className="font-bold text-amber-700 dark:text-amber-300">{completionPct} %</span> komplett — noch{' '}
                      <span className="font-bold text-amber-700 dark:text-amber-300">
                        {missing} {missing === 1 ? 'Pflichtfeld' : 'Pflichtfelder'}
                      </span>{' '}
                      offen
                    </React.Fragment>
                  );
                } else if (formData.vorname) {
                  parts.push(<React.Fragment key="hi">{greeting}schön dich zu sehen</React.Fragment>);
                }

                if (!isGuest && formData.beitrag_bezahlt === false) {
                  parts.push(
                    <React.Fragment key="beitrag-offen">
                      . Beitrag {formData.beitrag_jahr} ist{' '}
                      <span className="font-bold text-amber-700 dark:text-amber-300">noch offen</span>
                    </React.Fragment>
                  );
                } else if (!isGuest && formData.beitrag_bezahlt) {
                  parts.push(
                    <React.Fragment key="beitrag-ok">
                      . Beitrag {formData.beitrag_jahr}{' '}
                      <span className="font-bold text-primary-700 dark:text-primary-300">bezahlt</span>
                    </React.Fragment>
                  );
                }

                if (memberYears > 0) {
                  parts.push(
                    <React.Fragment key="member-years">
                      .{' '}
                      <span className="font-bold text-gray-900 dark:text-white">{memberYears} {memberYears === 1 ? 'Jahr' : 'Jahre'}</span>{' '}
                      dabei
                      {memberDays > 0 && (
                        <>
                          {' '}— <span className="font-bold text-gray-900 dark:text-white">{memberDays.toLocaleString('de-DE')} Tage</span> am Hundeplatz
                        </>
                      )}
                    </React.Fragment>
                  );
                }
              }
              return <>{parts}.</>;
            })()}
          </p>
        </section>

        {/* HERO PROFILE CARD — Split Layout */}
        <section className="pb-6">
          <article
            className="group relative rounded-3xl overflow-hidden bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
            style={{
              boxShadow:
                '0 1px 0 0 rgba(255,255,255,0.04) inset, 0 30px 60px -20px rgba(0,0,0,0.55)',
            }}
          >
            {/* SPLIT GRID */}
            <div className="grid md:grid-cols-[6fr_7fr]">
              {/* LEFT: Photo panel — portrait on mobile, full card height on desktop */}
              <div className="relative overflow-hidden aspect-[4/5] md:aspect-auto md:min-h-[520px]">
                {imageLoading ? (
                  <div className="w-full h-full flex items-center justify-center bg-primary-600">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-white" />
                  </div>
                ) : profileImageUrl && !imageError ? (
                  <img
                    src={profileImageUrl}
                    alt="Profilbild"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                    style={{ objectPosition: 'center 25%' }}
                    onError={() => setImageError(true)}
                  />
                ) : (
                  <div
                    className="w-full h-full flex items-center justify-center"
                    style={{
                      background:
                        'linear-gradient(135deg, rgba(16,185,129,0.20) 0%, rgba(2,6,23,0.40) 100%)',
                    }}
                  >
                    <UserIcon className="text-white/40 h-32 w-32" />
                  </div>
                )}

                {/* Mobile-only: bottom fade for overlaid pills/name */}
                <div
                  className="absolute inset-0 pointer-events-none md:hidden"
                  style={{
                    background:
                      'linear-gradient(180deg, rgba(2,6,23,0.05) 0%, rgba(2,6,23,0) 30%, rgba(2,6,23,0.55) 65%, rgba(28,36,50,0.98) 100%)',
                  }}
                />
                {/* Desktop-only: subtle vignette */}
                <div
                  className="hidden md:block absolute inset-0 pointer-events-none"
                  style={{
                    background:
                      'linear-gradient(180deg, rgba(2,6,23,0.30) 0%, rgba(2,6,23,0) 18%, rgba(2,6,23,0) 78%, rgba(2,6,23,0.45) 100%)',
                  }}
                />

                {/* Mobile-only: top pills (compact) */}
                <div className="md:hidden absolute top-0 left-0 right-0 z-20 flex items-center justify-between px-5 pt-5">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold backdrop-blur"
                      style={{
                        color: isComplete ? '#a7f3d0' : '#fde68a',
                        background: isComplete ? 'rgba(16,185,129,0.12)' : 'rgba(245,158,11,0.12)',
                        border: `1px solid ${
                          isComplete ? 'rgba(16,185,129,0.40)' : 'rgba(245,158,11,0.40)'
                        }`,
                      }}
                    >
                      <span
                        className="w-1.5 h-1.5 rounded-full animate-pulse"
                        style={{ background: isComplete ? '#34d399' : '#fbbf24' }}
                      />
                      {completionPct} %
                    </span>
                    {formData.vorstand === 1 && (
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold border border-primary-400/30 bg-primary-500/15 text-primary-300 backdrop-blur">
                        Vorstand
                      </span>
                    )}
                  </div>
                </div>

                {/* Mobile-only: name + story overlaid at bottom */}
                <div className="md:hidden absolute inset-x-0 bottom-0 px-5 pb-5 z-10">
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    {user?.mitglied_nr && (
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold border border-white/20 bg-white/10 text-white backdrop-blur">
                        {user?.mitglied_nr}
                      </span>
                    )}
                    {!isComplete && (
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold border border-amber-500/30 bg-amber-500/15 text-amber-300 backdrop-blur">
                        {completionFields.length - completedCount}{' '}
                        {completionFields.length - completedCount === 1 ? 'Feld' : 'Felder'} offen
                      </span>
                    )}
                  </div>
                  <h2
                    className="text-white font-black leading-none"
                    style={{
                      fontSize: 'clamp(2rem, 7vw, 2.5rem)',
                      letterSpacing: '-0.04em',
                      textShadow: '0 1px 2px rgba(0,0,0,0.55), 0 4px 18px rgba(0,0,0,0.55)',
                    }}
                  >
                    {`${formData.vorname || ''} ${formData.name || ''}`.trim() || 'Dein Name'}
                  </h2>
                  <p
                    className="text-white/95 text-sm leading-relaxed mt-2"
                    style={{
                      textShadow: '0 1px 2px rgba(0,0,0,0.55), 0 4px 18px rgba(0,0,0,0.55)',
                    }}
                  >
                    {formData.vereinsposition || 'Mitglied'}
                    {formData.eintritt && (
                      <>
                        {' '}
                        · seit{' '}
                        <span className="font-bold text-primary-300">
                          {formatLongDate(formData.eintritt)}
                        </span>
                      </>
                    )}
                  </p>
                </div>

                {/* Desktop-only: floating Mitglieds-ID badge + photo controls */}
                <div className="hidden md:flex absolute bottom-5 left-5 right-5 items-end justify-between gap-3 z-10">
                  {user?.mitglied_nr && (
                    <div
                      className="flex items-center gap-2.5 px-3 py-2 rounded-2xl backdrop-blur-md"
                      style={{
                        background: 'rgba(2,6,23,0.70)',
                        border: '1px solid rgba(255,255,255,0.10)',
                      }}
                    >
                      <div className="w-9 h-9 rounded-xl bg-primary-500/20 border border-primary-500/40 flex items-center justify-center">
                        <UserIcon className="w-4 h-4 text-primary-300" />
                      </div>
                      <div>
                        <div className="text-[9px] uppercase tracking-widest text-slate-300/90 font-bold">
                          Mitglieds-ID
                        </div>
                        <div className="text-sm font-black text-white tabular-nums">
                          {user?.mitglied_nr}
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    {profileImageUrl && !imageError && (
                      <button
                        onClick={handlePhotoDelete}
                        disabled={imageLoading}
                        className="w-10 h-10 rounded-xl backdrop-blur-md flex items-center justify-center text-rose-300 transition disabled:opacity-50"
                        style={{
                          background: 'rgba(2,6,23,0.70)',
                          border: '1px solid rgba(244,63,94,0.30)',
                        }}
                        title="Foto löschen"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    )}
                    <label
                      className="w-10 h-10 rounded-xl backdrop-blur-md flex items-center justify-center text-slate-200 transition cursor-pointer"
                      style={{
                        background: 'rgba(2,6,23,0.70)',
                        border: '1px solid rgba(255,255,255,0.10)',
                      }}
                      title="Foto wechseln"
                    >
                      <CameraIcon className="w-4 h-4" />
                      <input
                        id="image-upload"
                        type="file"
                        accept="image/*"
                        hidden
                        onChange={handlePhotoUpload}
                        disabled={imageLoading}
                      />
                    </label>
                  </div>
                </div>
              </div>

              {/* RIGHT: Content panel — Desktop only */}
              <div className="relative hidden md:flex flex-col justify-between p-8 lg:p-10">
                {/* Top: meta pills */}
                <div className="flex items-start gap-2 flex-wrap">
                  <span
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold"
                    style={{
                      color: isComplete ? '#a7f3d0' : '#fde68a',
                      background: isComplete ? 'rgba(16,185,129,0.12)' : 'rgba(245,158,11,0.12)',
                      border: `1px solid ${
                        isComplete ? 'rgba(16,185,129,0.40)' : 'rgba(245,158,11,0.40)'
                      }`,
                    }}
                  >
                    <span
                      className="w-1.5 h-1.5 rounded-full animate-pulse"
                      style={{ background: isComplete ? '#34d399' : '#fbbf24' }}
                    />
                    Profil zu {completionPct} %
                  </span>
                  {!isGuest && formData.eintritt && (
                    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold border border-white/15 bg-slate-950/55 text-slate-200">
                      Mitglied · seit {new Date(formData.eintritt).getFullYear()}
                    </span>
                  )}
                  {formData.vereinsposition && formData.vereinsposition !== 'Mitglied' && (
                    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold border border-primary-400/30 bg-primary-500/15 text-primary-300">
                      {formData.vereinsposition}
                    </span>
                  )}
                </div>

                {/* Middle: Name + Story */}
                <div className="my-6 lg:my-8">
                  <div className="flex flex-wrap items-center gap-2 mb-4">
                    {user?.mitglied_nr && (
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold border border-white/20 bg-white/10 text-white">
                        {user?.mitglied_nr}
                      </span>
                    )}
                    {!isComplete && (
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold border border-amber-500/30 bg-amber-500/15 text-amber-300">
                        {completionFields.length - completedCount}{' '}
                        {completionFields.length - completedCount === 1 ? 'Feld' : 'Felder'} offen
                      </span>
                    )}
                  </div>
                  <h2
                    className="text-gray-900 dark:text-white font-black tracking-tightest leading-[1.02]"
                    style={{
                      fontSize: 'clamp(2.5rem, 4vw, 4.5rem)',
                      letterSpacing: '-0.04em',
                    }}
                  >
                    {formData.vorname || 'Dein'}
                    <br />
                    <span className="text-primary-600 dark:text-primary-400">{formData.name || 'Name'}</span>
                  </h2>
                  <p className="text-gray-700 dark:text-slate-300 text-base lg:text-lg leading-relaxed mt-4 max-w-xl">
                    {formData.vereinsposition || 'Mitglied'}
                    {formData.eintritt && (
                      <>
                        {' '}
                        · Mitglied seit dem{' '}
                        <span className="font-bold text-primary-300">
                          {formatLongDate(formData.eintritt)}
                        </span>
                        {memberDays > 0 && (
                          <>
                            {' '}
                            · <span className="font-bold text-white">
                              {memberDays.toLocaleString('de-DE')} Tage
                            </span>{' '}
                            am Hundeplatz
                          </>
                        )}
                      </>
                    )}
                  </p>
                </div>

                {/* Bottom: 3-stat grid + completeness ring */}
                <div className="flex items-end justify-between gap-6">
                  <div className="grid grid-cols-3 gap-4 flex-1">
                    {!isGuest && (
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-slate-500">
                          Beitrag {formData.beitrag_jahr}
                        </div>
                        <div
                          className={`text-lg font-black tabular-nums mt-0.5 ${
                            formData.beitrag_bezahlt ? 'text-emerald-400' : 'text-amber-400'
                          }`}
                        >
                          {formData.beitrag_betrag
                            ? `${Number(formData.beitrag_betrag).toFixed(0)} €`
                            : '—'}
                        </div>
                        <div
                          className={`text-[10px] mt-0.5 ${
                            formData.beitrag_bezahlt ? 'text-emerald-400/80' : 'text-amber-400/80'
                          }`}
                        >
                          {formData.beitrag_bezahlt
                            ? `bezahlt ${
                                formData.beitrag_datum
                                  ? new Date(formData.beitrag_datum).toLocaleDateString('de-DE', {
                                      day: '2-digit',
                                      month: '2-digit',
                                    })
                                  : ''
                              }`
                            : 'nicht bezahlt'}
                        </div>
                      </div>
                    )}
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-slate-500">
                        Passkeys
                      </div>
                      <div className="text-lg font-black text-white tabular-nums mt-0.5">
                        {passkeys.length} aktiv
                      </div>
                      <div className="text-[10px] text-slate-400 mt-0.5 truncate">
                        {passkeys.length > 0
                          ? passkeys
                              .slice(0, 2)
                              .map((p) => p.device_name || 'Gerät')
                              .join(' · ')
                          : 'kein Gerät'}
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-slate-500">
                        Mitglied
                      </div>
                      <div className="text-lg font-black text-white tabular-nums mt-0.5">
                        {memberYears > 0 ? `${memberYears} J.` : '—'}
                      </div>
                      <div className="text-[10px] text-slate-400 mt-0.5">
                        {formData.eintritt
                          ? `seit ${String(new Date(formData.eintritt).getMonth() + 1).padStart(
                              2,
                              '0'
                            )}/${new Date(formData.eintritt).getFullYear()}`
                          : '—'}
                      </div>
                    </div>
                  </div>

                  {/* Completeness ring */}
                  <div className="shrink-0">
                    <div
                      className="relative w-24 h-24 rounded-full"
                      style={{
                        background: `conic-gradient(from 0deg, ${completionColor} 0deg ${completionDeg}deg, rgba(255,255,255,0.08) ${completionDeg}deg 360deg)`,
                        padding: 5,
                      }}
                    >
                      <div
                        className="w-full h-full rounded-full flex flex-col items-center justify-center"
                        style={{ background: CARD_BG, border: '1px solid #1e293b' }}
                      >
                        <div className="text-[9px] uppercase tracking-widest text-slate-400">
                          Profil
                        </div>
                        <div className="text-2xl font-black text-white leading-none">
                          {completionPct}
                        </div>
                        <div
                          className="text-[9px] font-semibold mt-0.5"
                          style={{ color: completionColor }}
                        >
                          % komplett
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer (full width, both viewports) */}
            <div
              className="relative px-5 sm:px-8 py-5 md:py-6"
              style={{ borderTop: '1px solid #1e293b' }}
            >
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6 items-center">
                <div className="hidden md:block">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500">
                    Mitglied seit
                  </div>
                  <div className="text-sm font-bold text-white mt-1 leading-tight">
                    {formData.eintritt ? formatLongDate(formData.eintritt) : '—'}
                    <br />
                    {user?.mitglied_nr && (
                      <span className="text-[11px] text-slate-400 font-medium">
                        {memberYears > 0 ? `${memberYears} Jahre · ` : ''}{user?.mitglied_nr}
                      </span>
                    )}
                  </div>
                </div>
                <div className="hidden md:block">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500">
                    Position
                  </div>
                  <div className="text-sm font-bold text-white mt-1">
                    {formData.vereinsposition || 'Mitglied'}
                  </div>
                </div>
                <div className="hidden md:block min-w-0">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500">
                    E-Mail
                  </div>
                  <div className="text-sm font-bold text-white mt-1 truncate" title={displayData.email}>
                    {displayData.email || '—'}
                  </div>
                  <div className="text-[11px] text-slate-400 font-medium">Login-Adresse</div>
                </div>

                {/* Mobile-only quick stats */}
                {!isGuest && (
                  <div className="md:hidden">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500">
                      Beitrag {formData.beitrag_jahr}
                    </div>
                    <div
                      className={`text-sm font-bold mt-0.5 ${
                        formData.beitrag_bezahlt ? 'text-emerald-400' : 'text-amber-400'
                      }`}
                    >
                      {formData.beitrag_bezahlt
                        ? `${Number(formData.beitrag_betrag || 0).toFixed(2)} € ✓`
                        : 'Offen'}
                    </div>
                  </div>
                )}
                <div className="md:hidden">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500">
                    Passkeys
                  </div>
                  <div className="text-sm font-bold text-white mt-0.5">
                    {passkeys.length} aktiv
                  </div>
                </div>

                {/* Action buttons */}
                <div className="col-span-2 md:col-span-1 flex gap-2">
                  {editMode ? (
                    <>
                      <button
                        onClick={() => setEditMode(false)}
                        disabled={loading}
                        className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-3 rounded-2xl text-slate-200 text-xs font-semibold transition disabled:opacity-50"
                        style={{
                          background: 'rgba(255,255,255,0.05)',
                          border: '1px solid rgba(255,255,255,0.10)',
                        }}
                      >
                        <XMarkIcon className="w-4 h-4" />
                        Abbrechen
                      </button>
                      <button
                        onClick={() => handleSubmit()}
                        disabled={loading}
                        className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-3 rounded-2xl bg-primary-500 hover:bg-primary-400 text-slate-950 text-xs font-bold transition disabled:opacity-50"
                      >
                        <CheckIcon className="w-4 h-4" />
                        Speichern
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => setEditMode(true)}
                      className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-3 rounded-2xl bg-primary-500 hover:bg-primary-400 text-slate-950 text-xs font-bold transition"
                    >
                      <PencilIcon className="w-4 h-4" />
                      Bearbeiten
                    </button>
                  )}
                </div>
              </div>
            </div>
          </article>
        </section>

        {/* BASISDATEN / KONTAKT / ADRESSE */}
        <section className="pb-6">
          <div className="grid md:grid-cols-3 gap-4">
            {/* BASISDATEN */}
            <article
              className="rounded-3xl p-6 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
              style={{
                boxShadow: '0 30px 60px -20px rgba(0,0,0,0.55)',
              }}
            >
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-xl bg-primary-500/10 border border-primary-500/30 flex items-center justify-center">
                  <UserIcon className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">
                    Basisdaten
                  </div>
                  <h4 className="text-base font-black text-gray-900 dark:text-white">Wer du bist</h4>
                </div>
              </div>
              <dl className="space-y-3">
                <FieldTile label="Vorname" value={displayData.vorname} />
                <FieldTile label="Nachname" value={displayData.name} />
                <FieldTile
                  label="Geburtsdatum"
                  value={
                    displayData.geb_am ? (
                      <>
                        {displayData.geb_am}
                        {formData.geb_am && (
                          <span className="text-[11px] text-slate-400 font-medium ml-2">
                            ·{' '}
                            {Math.floor(
                              (Date.now() - new Date(formData.geb_am).getTime()) /
                                (1000 * 60 * 60 * 24 * 365.25)
                            )}{' '}
                            Jahre
                          </span>
                        )}
                      </>
                    ) : (
                      ''
                    )
                  }
                />
                <FieldTile label="Geschlecht" value={genderLabel} />
              </dl>
            </article>

            {/* KONTAKT */}
            <article
              className="rounded-3xl p-6 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
              style={{
                boxShadow: '0 30px 60px -20px rgba(0,0,0,0.55)',
              }}
            >
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-xl bg-primary-500/10 border border-primary-500/30 flex items-center justify-center">
                  <svg
                    className="w-5 h-5 text-primary-600 dark:text-primary-400"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2}
                    viewBox="0 0 24 24"
                  >
                    <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z" />
                  </svg>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">Kontakt</div>
                  <h4 className="text-base font-black text-gray-900 dark:text-white">So erreicht man dich</h4>
                </div>
              </div>
              <dl className="space-y-3">
                <FieldTile
                  label="Telefon"
                  name="tel_nummer"
                  editable
                  value={displayData.tel_nummer}
                />
                <FieldTile
                  label="E-Mail"
                  value={displayData.email}
                  helper="Login-Adresse · nicht editierbar"
                />
              </dl>
            </article>

            {/* ADRESSE */}
            <article
              className="rounded-3xl p-6 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
              style={{
                boxShadow: '0 30px 60px -20px rgba(0,0,0,0.55)',
              }}
            >
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-xl bg-primary-500/10 border border-primary-500/30 flex items-center justify-center">
                  <svg
                    className="w-5 h-5 text-primary-600 dark:text-primary-400"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2}
                    viewBox="0 0 24 24"
                  >
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">Adresse</div>
                  <h4 className="text-base font-black text-gray-900 dark:text-white">Wo du wohnst</h4>
                </div>
              </div>
              <dl className="space-y-3">
                <FieldTile label="Straße" name="strasse" editable value={displayData.strasse} />
                <div className="grid grid-cols-3 gap-3">
                  <div className="col-span-1">
                    <FieldTile label="PLZ" name="plz" editable value={displayData.plz} />
                  </div>
                  <div className="col-span-2">
                    <FieldTile label="Ort" name="ort" editable value={displayData.ort} />
                  </div>
                </div>
              </dl>
            </article>
          </div>
        </section>

        {/* VEREINSDATEN + DATENSCHUTZ */}
        {!isGuest && (
          <section className="pb-6">
            <div className="grid lg:grid-cols-3 gap-6">
              {/* VEREINSDATEN */}
              <article
                className="lg:col-span-2 rounded-3xl p-6 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
                style={{
                  boxShadow: '0 30px 60px -20px rgba(0,0,0,0.55)',
                }}
              >
                <div className="flex items-start justify-between mb-6 gap-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-primary-500/10 border border-primary-500/30 flex items-center justify-center">
                      <svg
                        className="w-5 h-5 text-primary-600 dark:text-primary-400"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={2}
                        viewBox="0 0 24 24"
                      >
                        <rect x="3" y="5" width="18" height="16" rx="2" />
                        <path d="M3 9h18M8 3v4M16 3v4" />
                      </svg>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">
                        Vereinsdaten
                      </div>
                      <h4 className="text-xl font-black text-gray-900 dark:text-white">Deine Karriere im HSV</h4>
                    </div>
                  </div>
                  {formData.vereinsposition && (
                    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold border border-primary-500/30 bg-primary-500/10 text-primary-300">
                      {formData.vereinsposition}
                    </span>
                  )}
                </div>

                <div className="grid sm:grid-cols-2 gap-3">
                  <div
                    className="px-4 py-3 rounded-2xl bg-gray-50 dark:bg-slate-900/40 border border-gray-200 dark:border-slate-700/60"
                  >
                    <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">
                      Mitglied seit
                    </div>
                    <div className="text-lg font-black text-gray-900 dark:text-white tracking-tight mt-0.5">
                      {formData.eintritt ? formatLongDate(formData.eintritt) : '—'}
                    </div>
                    {memberDays > 0 && (
                      <div className="text-[11px] text-gray-600 dark:text-slate-400 mt-0.5">
                        {memberDays.toLocaleString('de-DE')} Tage · {memberYears} Jahre
                      </div>
                    )}
                  </div>
                  <div
                    className="px-4 py-3 rounded-2xl bg-gray-50 dark:bg-slate-900/40 border border-gray-200 dark:border-slate-700/60"
                  >
                    <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">
                      Position
                    </div>
                    <div className="text-lg font-black text-gray-900 dark:text-white tracking-tight mt-0.5">
                      {formData.vereinsposition || 'Mitglied'}
                    </div>
                    <div className="text-[11px] text-gray-600 dark:text-slate-400 mt-0.5">
                      {user?.mitglied_nr || '—'}
                    </div>
                  </div>
                  <div
                    className="px-4 py-3 rounded-2xl"
                    style={{
                      background: formData.beitrag_bezahlt
                        ? 'rgba(16,185,129,0.05)'
                        : 'rgba(245,158,11,0.05)',
                      border: formData.beitrag_bezahlt
                        ? '1px solid rgba(16,185,129,0.30)'
                        : '1px solid rgba(245,158,11,0.30)',
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div
                          className={`text-[10px] uppercase tracking-widest font-bold ${
                            formData.beitrag_bezahlt ? 'text-emerald-700 dark:text-emerald-300' : 'text-amber-700 dark:text-amber-300'
                          }`}
                        >
                          Beitrag {formData.beitrag_jahr}
                        </div>
                        <div className="text-lg font-black text-gray-900 dark:text-white tracking-tight mt-0.5">
                          {formData.beitrag_betrag
                            ? `${Number(formData.beitrag_betrag).toFixed(2)} €`
                            : '—'}
                        </div>
                        <div
                          className={`text-[11px] mt-0.5 ${
                            formData.beitrag_bezahlt ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'
                          }`}
                        >
                          {formData.beitrag_bezahlt
                            ? `bezahlt am ${
                                formData.beitrag_datum
                                  ? new Date(formData.beitrag_datum).toLocaleDateString('de-DE')
                                  : ''
                              }`
                            : 'nicht bezahlt'}
                        </div>
                      </div>
                      {formData.beitrag_bezahlt ? (
                        <CheckCircleIcon className="w-8 h-8 text-emerald-400" />
                      ) : (
                        <ExclamationTriangleIcon className="w-8 h-8 text-amber-400" />
                      )}
                    </div>
                  </div>
                  <div
                    className="px-4 py-3 rounded-2xl bg-gray-50 dark:bg-slate-900/40 border border-gray-200 dark:border-slate-700/60"
                  >
                    <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">
                      Beitrag {(Number(formData.beitrag_jahr) || new Date().getFullYear()) + 1}
                    </div>
                    <div className="text-lg font-black text-gray-900 dark:text-white tracking-tight mt-0.5">
                      {formData.beitrag_betrag
                        ? `${Number(formData.beitrag_betrag).toFixed(2)} €`
                        : '—'}
                    </div>
                    <div className="text-[11px] text-gray-600 dark:text-slate-400 mt-0.5">
                      fällig 01.01.{(Number(formData.beitrag_jahr) || new Date().getFullYear()) + 1}
                    </div>
                  </div>
                </div>
              </article>

              {/* DATENSCHUTZ */}
              <article
                className="relative rounded-3xl p-6 overflow-hidden bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
                style={{
                  boxShadow: '0 30px 60px -20px rgba(0,0,0,0.55)',
                }}
              >
                <div
                  className="absolute -top-12 -right-12 w-44 h-44 rounded-full pointer-events-none blur-2xl"
                  style={{ background: 'rgba(16,185,129,0.10)' }}
                />
                <div className="flex items-center gap-3 mb-5 relative">
                  <div className="w-10 h-10 rounded-xl bg-primary-500/10 border border-primary-500/30 flex items-center justify-center">
                    <svg
                      className="w-5 h-5 text-primary-600 dark:text-primary-400"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2}
                      viewBox="0 0 24 24"
                    >
                      <path d="M12 1l9 4v6c0 5.5-3.8 10.7-9 12-5.2-1.3-9-6.5-9-12V5l9-4z" />
                    </svg>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">
                      Datenschutz
                    </div>
                    <h4 className="text-base font-black text-gray-900 dark:text-white">Was öffentlich ist</h4>
                  </div>
                </div>

                <div className="space-y-2 relative">
                  <Toggle
                    active={formData.datenschutzerklaerung === 1}
                    label="DSGVO zugestimmt"
                    sublabel={
                      formData.datenschutzerklaerung === 1
                        ? 'Einwilligung erteilt'
                        : 'Noch nicht zugestimmt'
                    }
                  />
                  <Toggle
                    active={formData.homepage === 1}
                    label="Auf Homepage"
                    sublabel="Foto + Position sichtbar"
                  />
                  <Toggle
                    active={formData.facebook === 1}
                    label="Auf Facebook"
                    sublabel="Verein-Posts mit Foto"
                  />
                  <Toggle
                    active={formData.presse === 1}
                    label="In der Presse"
                    sublabel="Pressemitteilungen"
                  />
                </div>
              </article>
            </div>
          </section>
        )}

        {/* SECURITY */}
        <section className="pb-6">
          <div className="mb-5">
            <div className="text-[11px] uppercase tracking-[0.22em] text-primary-600/80 dark:text-primary-400/80 font-semibold">
              Sicherheit
            </div>
            <h3 className="text-2xl md:text-3xl font-black tracking-tight text-gray-900 dark:text-white mt-1">
              Login & Geräte
            </h3>
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            {/* PASSWORD */}
            <article
              className="rounded-3xl p-6 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
              style={{
                boxShadow: '0 30px 60px -20px rgba(0,0,0,0.55)',
              }}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-primary-500/10 border border-primary-500/30 flex items-center justify-center">
                  <svg
                    className="w-5 h-5 text-primary-600 dark:text-primary-400"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2}
                    viewBox="0 0 24 24"
                  >
                    <rect x="3" y="11" width="18" height="11" rx="2" />
                    <path d="M7 11V7a5 5 0 0110 0v4" />
                  </svg>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">
                    Passwort
                  </div>
                  <h4 className="text-base font-black text-gray-900 dark:text-white">Klassischer Login</h4>
                </div>
              </div>
              <p className="text-gray-600 dark:text-slate-400 text-sm mb-5">
                Aktualisiere dein Passwort regelmäßig — oder nutze einfach Passkeys für schnelleren
                und sichereren Login.
              </p>
              <button
                onClick={() => setShowPasswordModal(true)}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-gray-700 dark:text-slate-200 hover:bg-gray-100 dark:hover:bg-white/10 text-sm font-semibold transition bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10"
              >
                Passwort ändern
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  viewBox="0 0 24 24"
                >
                  <path d="M5 12h14M13 5l7 7-7 7" />
                </svg>
              </button>
            </article>

            {/* PASSKEYS */}
            <article
              className="lg:col-span-2 rounded-3xl p-6 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
              style={{
                boxShadow: '0 30px 60px -20px rgba(0,0,0,0.55)',
              }}
            >
              <div className="flex items-start justify-between mb-5 gap-3 flex-wrap">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-primary-500/10 border border-primary-500/30 flex items-center justify-center">
                    <KeyIcon className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">
                      Passkeys
                    </div>
                    <h4 className="text-base font-black text-gray-900 dark:text-white">Login ohne Passwort</h4>
                  </div>
                </div>
                {passkeySupported && (
                  <button
                    onClick={handleAddPasskey}
                    disabled={passkeyBusy}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-primary-500 hover:bg-primary-400 text-slate-950 text-xs font-bold transition disabled:opacity-50"
                  >
                    <svg
                      className="w-3.5 h-3.5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2.5}
                      viewBox="0 0 24 24"
                    >
                      <path d="M12 4v16m8-8H4" />
                    </svg>
                    Passkey hinzufügen
                  </button>
                )}
              </div>

              <div className="space-y-2.5">
                {passkeys.length === 0 && (
                  <div
                    className="px-4 py-6 rounded-2xl text-center bg-gray-50 dark:bg-slate-900/40 border border-gray-200 dark:border-slate-700/60"
                  >
                    <p className="text-sm text-gray-600 dark:text-slate-400">
                      {passkeySupported
                        ? 'Noch kein Passkey registriert. Schnapp dir einen — geht in 3 Sekunden.'
                        : 'Dein Browser unterstützt keine Passkeys.'}
                    </p>
                  </div>
                )}
                {passkeys.map((pk) => (
                  <div
                    key={pk.id}
                    className="px-4 py-3 rounded-2xl flex items-center gap-3 transition bg-gray-50 dark:bg-slate-900/40 border border-gray-200 dark:border-slate-700/60"
                  >
                    <div className="w-10 h-10 rounded-xl bg-primary-500/10 border border-primary-500/30 flex items-center justify-center shrink-0">
                      <KeyIcon className="w-5 h-5 text-primary-700 dark:text-primary-300" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="text-sm font-bold text-gray-900 dark:text-white truncate">
                          {pk.device_name || 'Unbenannter Passkey'}
                        </div>
                        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold border border-primary-500/30 bg-primary-500/10 text-primary-700 dark:text-primary-300">
                          <span className="w-1.5 h-1.5 rounded-full bg-primary-400 animate-pulse" />
                          Aktiv
                        </span>
                        {pk.backed_up ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border border-blue-500/30 bg-blue-500/10 text-blue-700 dark:text-blue-300">
                            iCloud Sync
                          </span>
                        ) : null}
                      </div>
                      <div className="text-[11px] text-gray-600 dark:text-slate-400 mt-0.5">
                        Erstellt {new Date(pk.created_at).toLocaleDateString('de-DE')}
                        {pk.last_used_at && (
                          <>
                            {' '}
                            · zuletzt {new Date(pk.last_used_at).toLocaleDateString('de-DE')}
                          </>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => handleDeletePasskey(pk.id)}
                      disabled={passkeyBusy}
                      className="w-9 h-9 rounded-lg flex items-center justify-center text-gray-500 dark:text-slate-500 hover:bg-rose-500/10 hover:text-rose-600 dark:hover:text-rose-400 transition disabled:opacity-50"
                      aria-label="Entfernen"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>

              {passkeySupported && (
                <div
                  className="mt-5 pt-4 flex items-start gap-2 text-[11px] text-gray-600 dark:text-slate-400 border-t border-gray-200 dark:border-slate-800"
                >
                  <svg
                    className="w-4 h-4 text-primary-400 shrink-0 mt-0.5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2}
                    viewBox="0 0 24 24"
                  >
                    <circle cx="12" cy="12" r="9" />
                    <path d="M12 8v4M12 16h.01" />
                  </svg>
                  <span>
                    Passkeys werden über iCloud / Google synchronisiert. Verlierst du dein Gerät,
                    erstellst du auf einem neuen einfach einen neuen Passkey — deine Karte bleibt
                    sicher.
                  </span>
                </div>
              )}
            </article>
          </div>
        </section>
      </div>

      {/* PASSWORD MODAL */}
      {showPasswordModal && (
        <div
          className="fixed inset-0 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4"
          style={{ background: 'rgba(2,6,23,0.65)', backdropFilter: 'blur(4px)' }}
          onClick={() => {
            setShowPasswordModal(false);
            setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
          }}
        >
          <div
            className="rounded-t-3xl sm:rounded-3xl w-full sm:max-w-[480px] flex flex-col overflow-hidden"
            style={{
              background: CARD_BG,
              border: '1px solid #334155',
              boxShadow: '0 30px 60px -20px rgba(0,0,0,0.7)',
              maxHeight: 'calc(100vh - 32px)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 pt-6 pb-4 flex items-start justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-primary-300 font-bold">
                  Sicherheit
                </div>
                <h2 className="text-2xl font-black text-white tracking-tight mt-1">
                  Passwort ändern
                </h2>
                <p className="text-slate-400 text-sm mt-1">
                  Mindestens 10 Zeichen, gemischt aus Buchstaben, Zahlen und Sonderzeichen.
                </p>
              </div>
              <button
                onClick={() => {
                  setShowPasswordModal(false);
                  setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
                }}
                className="w-9 h-9 rounded-full flex items-center justify-center transition"
                style={{
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.10)',
                }}
                aria-label="Schließen"
              >
                <XMarkIcon className="w-4 h-4 text-slate-300" />
              </button>
            </div>

            <div className="px-6 py-5 overflow-y-auto flex-1 space-y-4">
              <label className="block">
                <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
                  Aktuelles Passwort
                </span>
                <input
                  type="password"
                  value={passwordData.currentPassword}
                  onChange={(e) =>
                    setPasswordData({ ...passwordData, currentPassword: e.target.value })
                  }
                  className="mt-1 w-full px-3 py-2.5 rounded-xl text-sm font-bold text-white outline-none transition"
                  style={{
                    background: 'rgba(15,23,42,0.6)',
                    border: '1px solid #334155',
                  }}
                />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
                  Neues Passwort
                </span>
                <input
                  type="password"
                  value={passwordData.newPassword}
                  onChange={(e) =>
                    setPasswordData({ ...passwordData, newPassword: e.target.value })
                  }
                  className="mt-1 w-full px-3 py-2.5 rounded-xl text-sm font-bold text-white outline-none transition"
                  style={{
                    background: 'rgba(15,23,42,0.6)',
                    border: '1px solid #334155',
                  }}
                />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
                  Neues Passwort bestätigen
                </span>
                <input
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={(e) =>
                    setPasswordData({ ...passwordData, confirmPassword: e.target.value })
                  }
                  className="mt-1 w-full px-3 py-2.5 rounded-xl text-sm font-bold text-white outline-none transition"
                  style={{
                    background: 'rgba(15,23,42,0.6)',
                    border: '1px solid #334155',
                  }}
                />
              </label>
            </div>

            <div
              className="px-6 py-5 flex flex-col-reverse sm:flex-row sm:justify-end gap-2"
              style={{ borderTop: '1px solid #1e293b', background: 'rgba(2,6,23,0.4)' }}
            >
              <button
                onClick={() => {
                  setShowPasswordModal(false);
                  setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
                }}
                className="px-4 py-3 rounded-xl text-slate-200 text-sm font-semibold transition"
                style={{
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.10)',
                }}
              >
                Abbrechen
              </button>
              <button
                onClick={handlePasswordChange}
                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-primary-500 hover:bg-primary-400 text-slate-950 text-sm font-bold transition"
              >
                Ändern
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmationModal
        visible={confirmModal.visible}
        onClose={() => setConfirmModal({ ...confirmModal, visible: false })}
        type={confirmModal.type}
        title={confirmModal.title}
        message={confirmModal.message}
        variant={confirmModal.variant}
        button={
          confirmModal.variant === 'single'
            ? {
                text: 'OK',
                onPress: () => {
                  confirmModal.onConfirm();
                  setConfirmModal({ ...confirmModal, visible: false });
                },
              }
            : undefined
        }
        buttons={
          confirmModal.variant === 'two'
            ? {
                cancel: {
                  text: 'Abbrechen',
                  onPress: () => setConfirmModal({ ...confirmModal, visible: false }),
                },
                confirm: {
                  text: 'Bestätigen',
                  onPress: () => {
                    confirmModal.onConfirm();
                    setConfirmModal({ ...confirmModal, visible: false });
                  },
                },
              }
            : undefined
        }
      />
    </div>
  );
};

export default Profile;
